import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { MessageCircle, Send, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface ContactCallbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ContactCallbackModal({ open, onOpenChange }: ContactCallbackModalProps) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim() || !phone.trim()) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }

    setIsSubmitting(true);

    try {
      // Call edge function which handles rate limiting + insert + notification
      const { data, error } = await supabase.functions.invoke('notify-contact-request', {
        body: { 
          name: name.trim(), 
          phone: phone.trim(), 
          message: message.trim() || undefined 
        }
      });

      if (error) {
        // Check if it's a rate limit error (429)
        if (error.message?.includes('429') || data?.rateLimited) {
          toast.error('Trop de demandes en peu de temps. Merci de réessayer dans quelques minutes.');
          return;
        }
        throw error;
      }

      // Check response for rate limit
      if (data?.rateLimited) {
        toast.error(data.error || 'Trop de demandes en peu de temps. Merci de réessayer dans quelques minutes.');
        return;
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      setIsSuccess(true);
      toast.success('Demande envoyée ! Nous vous rappellerons bientôt.');
      
      // Reset after delay
      setTimeout(() => {
        setName('');
        setPhone('');
        setMessage('');
        setIsSuccess(false);
        onOpenChange(false);
      }, 2000);
    } catch (error: any) {
      console.error('Error submitting contact request:', error);
      
      // Handle FunctionsHttpError for 429
      if (error?.context?.status === 429) {
        toast.error('Trop de demandes en peu de temps. Merci de réessayer dans quelques minutes.');
        return;
      }
      
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-primary" />
            Demande de rappel
          </DialogTitle>
          <DialogDescription>
            Laissez vos coordonnées et nous vous rappellerons dans les plus brefs délais.
          </DialogDescription>
        </DialogHeader>

        {isSuccess ? (
          <div className="flex flex-col items-center justify-center py-8 gap-4">
            <div className="h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <p className="text-center font-medium">Demande envoyée avec succès !</p>
            <p className="text-center text-sm text-muted-foreground">
              Nous vous rappellerons bientôt.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom *</Label>
              <Input
                id="name"
                placeholder="Votre nom"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Téléphone / WhatsApp *</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+33 6 12 34 56 78"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Message (optionnel)</Label>
              <Textarea
                id="message"
                placeholder="Décrivez brièvement votre besoin..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
              />
            </div>

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? (
                'Envoi en cours...'
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Envoyer ma demande
                </>
              )}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
